import { DataExportType } from "@/api-schema-enums";
import type { ServerPaginationState } from "@/components/data-list";
import { DataList } from "@/components/data-list";
import { DataExportButton } from "@/components/data-table/predefined/_components/data-export-button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { HammerIcon, ScaleIcon, SquareStack, TimerIcon, UsersIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserFilter, type ExtendedUser } from "../_hooks/use-group-users";
import { GroupUserCard } from "./group-user-card";

const EXPORT_COLUMNS = ["firstName", "lastName", "departments.name", "occupations.name", "email"];

interface GroupUserSectionProps {
  groupUuid: string;
  canManage: boolean;
  filteredUsers: ExtendedUser[];
  userFilter: UserFilter;
  onUserFilterChange: (filter: UserFilter) => void;
  userSearchTerm: string;
  onSearchChange: (value: string) => void;
  userStats: { total: number; rule: number; temporary: number; duplicate: number; manual: number };
  isLoading: boolean;
  paginationState: ServerPaginationState;
  onPaginationChange: (state: ServerPaginationState) => void;
}

export function GroupUserSection({
  groupUuid,
  canManage,
  filteredUsers,
  userFilter,
  onUserFilterChange,
  userSearchTerm,
  onSearchChange,
  userStats,
  isLoading,
  paginationState,
  onPaginationChange,
}: GroupUserSectionProps) {
  const { t } = useTranslation(["ram", "common"]);

  const renderUser = useCallback((user: ExtendedUser) => <GroupUserCard key={user.uuid} user={user} />, []);

  const handleSearchChange = useCallback((value: string) => onSearchChange(value), [onSearchChange]);

  const dataListActions = useMemo(
    () => ({
      onSearchChange: handleSearchChange,
      onPaginationChange,
    }),
    [onPaginationChange, handleSearchChange],
  );

  const dataListFeatures = useMemo(
    () => ({
      search: true,
      pagination: true,
      multiSelection: false,
      sorting: false,
    }),
    [],
  );

  const emptyStateComponent = useMemo(
    () => (
      <div className="text-muted-foreground py-12 text-center">
        {userSearchTerm ? t("ram:NoUsersFound") : t("ram:NoUsersInThisGroup")}
      </div>
    ),
    [userSearchTerm, t],
  );

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg leading-none font-semibold">{t("ram:Users")}</h2>
            <p className="text-muted-foreground mt-1 text-sm">{t("ram:MembersOfThisGroup")}</p>
          </div>
          {userFilter === UserFilter.All && (
            <DataExportButton
              type={DataExportType.GroupMember}
              query={{}}
              body={{ group: groupUuid }}
              defaultColumns={EXPORT_COLUMNS}
            />
          )}
        </div>

        <div className="flex w-full items-center">
          <ToggleGroup
            className="w-full flex-wrap justify-start gap-2"
            type="single"
            value={userFilter}
            onValueChange={(value) => {
              if (!value) {
                onUserFilterChange(UserFilter.All);
                return;
              }
              onUserFilterChange(value as UserFilter);
            }}
            aria-label={t("ram:FilterUsers")}
            size="sm"
          >
            <ToggleGroupItem
              value={UserFilter.All}
              className="data-[state=on]:border-primary data-[state=on]:bg-primary/10 rounded-md px-2.5 data-[variant=outlined]:border-l"
              variant="outlined"
            >
              <UsersIcon className="text-primary mr-1 h-4 w-4" />
              {t("ram:Total")} ({userStats.total})
            </ToggleGroupItem>

            <ToggleGroupItem
              value={UserFilter.Rule}
              className="rounded-md px-2.5 data-[state=on]:border-blue-500 data-[state=on]:bg-blue-500/10 data-[variant=outlined]:border-l"
              variant="outlined"
            >
              <ScaleIcon className="mr-1 h-4 w-4 text-blue-500" />
              {t("ram:RuleBased")} ({userStats.rule})
            </ToggleGroupItem>

            <ToggleGroupItem
              value={UserFilter.Temporary}
              className="rounded-md px-2.5 data-[state=on]:border-cyan-500 data-[state=on]:bg-cyan-500/10 data-[variant=outlined]:border-l"
              variant="outlined"
            >
              <TimerIcon className="mr-1 h-4 w-4 text-cyan-500" />
              {t("ram:Temporary")} ({userStats.temporary})
            </ToggleGroupItem>

            <ToggleGroupItem
              value={UserFilter.Manual}
              className="data-[state=on]:border-success data-[state=on]:bg-success/10 rounded-md px-2.5 data-[variant=outlined]:border-l"
              variant="outlined"
            >
              <HammerIcon className="text-success mr-1 h-4 w-4" />
              {t("common:Manually")} ({userStats.manual})
            </ToggleGroupItem>

            <ToggleGroupItem
              value={UserFilter.Duplicate}
              className="data-[state=on]:border-warning data-[state=on]:bg-warning/10 rounded-md px-2.5 data-[variant=outlined]:border-l"
              variant="outlined"
            >
              <SquareStack className="text-warning mr-1 h-4 w-4" />
              {t("ram:Duplicates")} ({userStats.duplicate})
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div>
        <DataList
          data={filteredUsers}
          itemRenderer={renderUser}
          searchTerm={userSearchTerm}
          searchPlaceholder={t("common:SearchUser")}
          isLoading={isLoading}
          itemLayout="grid"
          paginationState={paginationState}
          containerClassName="max-h-96 overflow-y-auto"
          features={dataListFeatures}
          actions={dataListActions}
          emptyStateComponent={emptyStateComponent}
          createButtonLink={canManage ? `~/ram/group/${groupUuid}/user` : undefined}
          createButtonText={t("ram:ManageManuallyAddedUsers")}
        />
      </div>
    </div>
  );
}
